/**
 * Phase 8 — Utility Vault (creator-facing).
 * Browse approved utilities and attach them to a Kickback/portfolio.
 * Uses the new backend endpoints:
 *   GET  /api/creator/utility-vault
 *   POST /api/creator/portfolios/{portfolio_id}/utilities/attach-approved
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Filter, Link2, Info } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card,
  border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card,
  padding: 20,
};
const inputStyle = {
  width: '100%', padding: '8px 10px', border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

const TYPE_OPTIONS = [
  '', 'discount', 'digital_resource', 'exclusive_content', 'service_credit',
  'consultation', 'event_access', 'community_access', 'product_bonus',
  'membership_benefit', 'employee_benefit', 'referral_reward',
  'partner_offer', 'physical_perk', 'other',
];

export default function UtilityVaultPage() {
  const [utilities, setUtilities] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState({});
  const [filterType, setFilterType] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [attaching, setAttaching] = useState({});
  const [planWarning, setPlanWarning] = useState(null);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }), []);

  const loadVault = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) params.utility_type = filterType;
      if (q) params.q = q;
      const res = await axios.get(`${API}/creator/utility-vault`, { ...auth, params });
      setUtilities(res.data?.utilities || []);
      if (!res.data?.plan?.synced) setPlanWarning(res.data?.plan?.warning || null);
      else setPlanWarning(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load vault');
    } finally {
      setLoading(false);
    }
  }, [auth, filterType, q]);

  const loadPortfolios = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/creator/portfolios`, auth);
      setPortfolios(res.data?.portfolios || []);
    } catch (e) {
      // silent — attach button will still render but portfolios list may be empty
    }
  }, [auth]);

  useEffect(() => { loadVault(); }, [loadVault]);
  useEffect(() => { loadPortfolios(); }, [loadPortfolios]);

  const attach = async (utilityId) => {
    const portfolioId = selectedPortfolio[utilityId];
    if (!portfolioId) {
      toast.error('Choose a Kickback first');
      return;
    }
    setAttaching(prev => ({ ...prev, [utilityId]: true }));
    try {
      const res = await axios.post(
        `${API}/creator/portfolios/${portfolioId}/utilities/attach-approved`,
        { utility_id: utilityId },
        auth,
      );
      if (res.data?.duplicate) toast.info(res.data.message || 'Already attached');
      else toast.success('Utility attached to Kickback');
      if (res.data?.plan_warning) toast.info(res.data.plan_warning);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Attach failed');
    } finally {
      setAttaching(prev => ({ ...prev, [utilityId]: false }));
    }
  };

  return (
    <div data-testid="utility-vault-page" style={{ padding: 40 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Package size={24} color={theme.colors.primary} />
          <h1 style={{ fontSize: 26, fontWeight: 700, color: theme.colors.text.primary,
            fontFamily: theme.fonts.heading, margin: 0 }}>Utility Vault</h1>
        </div>
        <p style={{ fontSize: 14, color: theme.colors.text.secondary, marginTop: 6 }}>
          Browse approved utilities, perks, services, rewards, and access offers that
          can be attached to your Kickbacks.
        </p>
        {planWarning && (
          <div data-testid="vault-plan-warning" style={{ marginTop: 10, padding: 10,
            background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6,
            color: '#78350F', fontSize: 12, display: 'inline-flex', gap: 6,
            alignItems: 'center' }}>
            <Info size={13} /> {planWarning}
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{ ...cardStyle, marginBottom: 20, display: 'flex', gap: 10,
        flexWrap: 'wrap', alignItems: 'center' }}>
        <Filter size={16} color={theme.colors.text.muted} />
        <select data-testid="vault-filter-type" style={{ ...inputStyle, width: 220 }}
          value={filterType} onChange={e => setFilterType(e.target.value)}>
          {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t || 'All types'}</option>)}
        </select>
        <input data-testid="vault-search" style={{ ...inputStyle, flex: 1, minWidth: 240 }}
          placeholder="Search name or description…" value={q}
          onChange={e => setQ(e.target.value)} />
      </div>

      {/* Utilities grid */}
      {loading ? (
        <div data-testid="vault-loading" style={{ color: theme.colors.text.muted,
          padding: 40, textAlign: 'center' }}>Loading vault…</div>
      ) : utilities.length === 0 ? (
        <div data-testid="vault-empty" style={{ ...cardStyle, textAlign: 'center',
          color: theme.colors.text.muted }}>
          No approved utilities available yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {utilities.map(u => (
            <div key={u.id} data-testid={`vault-card-${u.id}`} style={cardStyle}>
              <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text.primary,
                marginBottom: 4 }}>{u.utility_name}</div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  padding: '2px 8px', borderRadius: 999, background: `${theme.colors.primary}1A`,
                  color: theme.colors.primary }}>{u.utility_type}</span>
                {u.free_or_paid && (
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                    padding: '2px 8px', borderRadius: 999,
                    background: '#F3F4F6', color: '#374151' }}>{u.free_or_paid}</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: theme.colors.text.secondary,
                marginBottom: 10, minHeight: 40 }}>{u.utility_description}</div>
              {u.provider_name && (
                <div style={{ fontSize: 11, color: theme.colors.text.muted, marginBottom: 8 }}>
                  Provider: <b>{u.provider_name}</b>
                </div>
              )}
              {u.eligibility_rules && (
                <div style={{ fontSize: 11, color: theme.colors.text.muted, marginBottom: 4 }}>
                  <b>Eligibility:</b> {u.eligibility_rules}
                </div>
              )}
              {u.limits_conditions && (
                <div style={{ fontSize: 11, color: theme.colors.text.muted, marginBottom: 4 }}>
                  <b>Limits:</b> {u.limits_conditions}
                </div>
              )}
              {u.expiration_rules && (
                <div style={{ fontSize: 11, color: theme.colors.text.muted, marginBottom: 8 }}>
                  <b>Expiration:</b> {u.expiration_rules}
                </div>
              )}
              <div style={{ borderTop: `1px solid ${theme.colors.borders}`, marginTop: 10,
                paddingTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <select data-testid={`vault-portfolio-select-${u.id}`}
                  style={{ ...inputStyle, flex: 1, minWidth: 160 }}
                  value={selectedPortfolio[u.id] || ''}
                  onChange={e => setSelectedPortfolio(prev => ({ ...prev, [u.id]: e.target.value }))}>
                  <option value="">Choose a Kickback…</option>
                  {portfolios.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <button data-testid={`vault-attach-${u.id}`}
                  disabled={attaching[u.id]} onClick={() => attach(u.id)}
                  style={{ padding: '8px 12px', borderRadius: theme.radius.sm,
                    border: 'none', background: theme.colors.primary, color: '#FFF',
                    cursor: attaching[u.id] ? 'not-allowed' : 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontWeight: 700, fontSize: 12 }}>
                  <Link2 size={12} /> {attaching[u.id] ? 'Attaching…' : 'Attach to Kickback'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
